import React, { useState, useEffect } from "react";
import "./index.css";
import { FaEdit, FaTrash } from "react-icons/fa";
import Select from "react-select";
import {
  InitiatorTypeOptions,
  OperationOptions,
  ModeOptions,
  InputTypeOptions,
  ProviderOptions,
  EvaluationModeOptions,
  ExpressionTypeOptions,
  OnStatusOptions,
} from "../../Utils/constants";
import { useAssetNameSearch } from "../../API/Queries/useAssetNameSearch";

const ActionsModal = ({ onClose, onSaveAction, existingAction = null }) => {
  const [form, setForm] = useState({
    name: "",
    initiatorType: "",
    operation: "",
    mode: "",
    inputType: "",
    provider: "",
    group: "",
    actionGroup: "",
    event: {
      callbackUrl: "",
      eventData: [
        { onStatus: "", entity: "", entityId: "" }
      ]
    },
  });

  const [digitalAssetUpdateConfig, setDigitalAssetUpdateConfig] = useState({
    ruleConfig: {
      name: "",
      evaluationMode: "",
      expressions: [],
    },
    serviceConfigName: "",
    requiredAssetDefinitionNames: [{ name: "", id: "" }],
  });

  const [expressionForm, setExpressionForm] = useState({
    type: "",
    lhs: "",
    rhs: "",
    validationFailureMessage: "",
  });
  const [editingExpressionIndex, setEditingExpressionIndex] = useState(null);
  const [showExpressionsPopup, setShowExpressionsPopup] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [assetOptions, setAssetOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const { mutate: fetchAssetNames, isLoading: isAssetLoading } =
    useAssetNameSearch();

  const requiredFields = [
    "name",
    "initiatorType",
    "operation",
    "mode",
    "inputType",
    "provider",
  ];

  useEffect(() => {
    if (existingAction) {
      setForm({ ...existingAction });
      setDigitalAssetUpdateConfig(
        existingAction.digitalAssetUpdateConfig || {
          ruleConfig: { name: "", evaluationMode: "", expressions: [] },
          serviceConfigName: "",
          requiredAssetDefinitionNames: [{ name: "", id: "" }],
        }
      );
    }
  }, [existingAction]);

  useEffect(() => {
    if (existingAction) {
      setForm({
        name: existingAction.name || "",
        initiatorType: existingAction.initiatorType || "",
        operation: existingAction.operation || "",
        mode: existingAction.mode || "",
        inputType: existingAction.inputType || "",
        provider: existingAction.provider || "",
        group: existingAction.group || "",
        actionGroup: existingAction.actionGroup || "",
        event: {
          callbackUrl: existingAction?.event?.callbackUrl || "",
          eventData:
            existingAction?.event?.eventData?.length > 0
              ? existingAction.event.eventData.map((d) => ({
                  onStatus: d?.onStatus || "",
                  entity: d?.entity || "",
                  entityId: d?.entityId || "",
                }))
              : [{ onStatus: "", entity: "", entityId: "" }],
        },
        
      });

      setDigitalAssetUpdateConfig({
        ruleConfig: {
          name:
            existingAction?.digitalAssetUpdateConfig?.ruleConfig?.name || "",
          evaluationMode:
            existingAction?.digitalAssetUpdateConfig?.ruleConfig
              ?.evaluationMode || "",
          expressions:
            existingAction?.digitalAssetUpdateConfig?.ruleConfig?.expressions ||
            [],
        },
        serviceConfigName:
          existingAction?.digitalAssetUpdateConfig?.serviceConfigName || "",
        requiredAssetDefinitionNames: existingAction?.digitalAssetUpdateConfig
          ?.requiredAssetDefinitionNames?.length
          ? existingAction.digitalAssetUpdateConfig.requiredAssetDefinitionNames.map(
              (item) => ({
                name: item?.name || "",
                id: item?.id || "",
              })
            )
          : [{ name: "", id: "" }],
      });
    }
  }, [existingAction]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleSave = () => {
    const newErrors = {};
    requiredFields.forEach((field) => {
      if (!form[field]) {
        newErrors[field] = "This field is required";
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const formattedRetryConfig =
      form.retryConfig &&
      (form.retryConfig.purpose || form.retryConfig.maxRetryCount)
        ? {
            ...form.retryConfig,
            currentRetryCount: form.retryConfig.currentRetryCount || 0,
          }
        : null;

    onSaveAction({
      ...form,
      digitalAssetUpdateConfig,
      retryConfig: formattedRetryConfig,
    });

    onClose();
  };

  const handleSaveExpression = () => {
    setDigitalAssetUpdateConfig((prev) => ({
      ...prev,
      ruleConfig: {
        ...prev.ruleConfig,
        expressions: [...prev.ruleConfig.expressions, expressionForm],
      },
    }));

    setExpressionForm({
      type: "",
      lhs: "",
      rhs: "",
      validationFailureMessage: "",
    });

    setShowExpressionsPopup(false);
  };

  const handleAddOrUpdateExpression = () => {
    const updatedExpressions = [
      ...digitalAssetUpdateConfig.ruleConfig.expressions,
    ];
    if (editingExpressionIndex !== null) {
      updatedExpressions[editingExpressionIndex] = expressionForm;
    } else {
      updatedExpressions.push(expressionForm);
    }
    setDigitalAssetUpdateConfig((prev) => ({
      ...prev,
      ruleConfig: {
        ...prev.ruleConfig,
        expressions: updatedExpressions,
      },
    }));
    setShowExpressionsPopup(false);
    setExpressionForm({
      type: "",
      lhs: "",
      rhs: "",
      validationFailureMessage: "",
    });
    setEditingExpressionIndex(null);
  };

  const handleEditExpression = (index) => {
    setEditingExpressionIndex(index);
    setExpressionForm(digitalAssetUpdateConfig.ruleConfig.expressions[index]);
    setShowExpressionsPopup(true);
  };

  const handleDeleteExpression = (index) => {
    const updated = digitalAssetUpdateConfig.ruleConfig.expressions.filter(
      (_, i) => i !== index
    );
    setDigitalAssetUpdateConfig((prev) => ({
      ...prev,
      ruleConfig: { ...prev.ruleConfig, expressions: updated },
    }));
  };

  const handleEventChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      event: { ...prev.event, [field]: value },
    }));
  };

  const handleEventDataChange = (index, field, value) => {
    const updated = [...form.event.eventData];
    updated[index][field] = value;
    setForm((prev) => ({
      ...prev,
      event: { ...prev.event, eventData: updated },
    }));
  };

  return (
    <div className="actions-modal-overlay">
      <div className="actions-modal-box">
        <div className="actions-modal-header">
          <h3>Add Action</h3>
          <span className="actions-modal-close" onClick={onClose}>
            ×
          </span>
        </div>

        <div className="actions-modal-content">
          <div className="actions-modal-field-row">
            <label>
              Name <span style={{ color: "red" }}>*</span>
            </label>
            <input
              type="text"
              placeholder="Enter action name"
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
            />
          </div>
          {errors.name && (
            <div className="input-error-message">{errors.name}</div>
          )}

          <div className="actions-modal-field-row">
            <label>
              Initiator Type <span style={{ color: "red" }}>*</span>
            </label>
            <div style={{ width: "62%" }}>
              <Select
                value={InitiatorTypeOptions.find(
                  (option) => option.value === form.initiatorType
                )}
                onChange={(selected) =>
                  handleChange("initiatorType", selected?.value || "")
                }
                options={InitiatorTypeOptions}
                classNamePrefix="react-select"
              />
            </div>
            {errors.initiatorType && (
              <div className="input-error-message">{errors.initiatorType}</div>
            )}
          </div>

          <div className="actions-modal-field-row">
            <label>
              Operation <span style={{ color: "red" }}>*</span>
            </label>
            <div style={{ width: "62%" }}>
              <Select
                value={OperationOptions.find(
                  (option) => option.value === form.operation
                )}
                onChange={(selected) =>
                  handleChange("operation", selected?.value || "")
                }
                options={OperationOptions}
                classNamePrefix="react-select"
              />
            </div>
            {errors.operation && (
              <div className="input-error-message">{errors.operation}</div>
            )}
          </div>

          <div className="actions-modal-field-row">
            <label>
              Mode <span style={{ color: "red" }}>*</span>
            </label>
            <div style={{ width: "62%" }}>
              <Select
                value={ModeOptions.find((option) => option.value === form.mode)}
                onChange={(selected) =>
                  handleChange("mode", selected?.value || "")
                }
                options={ModeOptions}
                classNamePrefix="react-select"
              />
            </div>
            {errors.mode && (
              <div className="input-error-message">{errors.mode}</div>
            )}
          </div>

          <div className="actions-modal-field-row">
            <label>
              Input Type <span style={{ color: "red" }}>*</span>
            </label>
            <div style={{ width: "62%" }}>
              <Select
                value={InputTypeOptions.find(
                  (option) => option.value === form.inputType
                )}
                onChange={(selected) =>
                  handleChange("inputType", selected?.value || "")
                }
                options={InputTypeOptions}
                classNamePrefix="react-select"
              />
            </div>
            {errors.inputType && (
              <div className="input-error-message">{errors.inputType}</div>
            )}
          </div>

          <div className="actions-modal-field-row">
            <label>
              Provider <span style={{ color: "red" }}>*</span>
            </label>
            <div style={{ width: "62%" }}>
              <Select
                value={ProviderOptions.find(
                  (option) => option.value === form.provider
                )}
                onChange={(selected) =>
                  handleChange("provider", selected?.value || "")
                }
                options={ProviderOptions}
                classNamePrefix="react-select"
              />
            </div>
            {errors.provider && (
              <div className="input-error-message">{errors.provider}</div>
            )}
          </div>

          <div className="actions-modal-field-row">
            <label>Action Group</label>
            <input
              type="text"
              value={form.actionGroup}
              onChange={(e) => handleChange("actionGroup", e.target.value)}
            />
          </div>

          <div className="actions-modal-field-row">
            <label>Event</label>
          </div>

          <div className="actions-modal-field">
            <label>. callbackUrl</label>
            <input
              type="text"
              value={form.event.callbackUrl}
              onChange={(e) => handleEventChange("callbackUrl", e.target.value)}
            />
          </div>

          <div className="actions-modal-subsections">
            <label>EventData</label>
            {form.event.eventData.map((entry, index) => (
              <div key={index}>
                <div className="actions-modal-field">
                  <label>. onStatus</label>
                  <div style={{ width: "66%" }}>
                  <Select
                    options={OnStatusOptions}
                    value={OnStatusOptions.find((opt) => opt.value === entry.onStatus)}
                    onChange={(selected) =>
                      handleEventDataChange(index, "onStatus", selected?.value || "")
                    }
                    classNamePrefix="react-select"
                  />
                  </div>
                </div>
                <div className="actions-modal-field">
                  <label>. entity</label>
                  <input
                    type="text"
                    style={{ marginTop: "10px" }}
                    value={entry.entity}
                    onChange={(e) => handleEventDataChange(index, "entity", e.target.value)}
                  />
                </div>
                <div className="actions-modal-field">
                  <label>. entityId</label>
                  <input
                    type="text"
                    style={{ marginTop: "10px" }}
                    value={entry.entityId}
                    onChange={(e) => handleEventDataChange(index, "entityId", e.target.value)}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="actions-modal-field-row">
            <label>Digital Asset Update Config</label>
          </div>

          <div className="actions-modal-subsection">
            <label>1. RuleConfig</label>

            <div className="actions-modal-field">
              <label>. name</label>
              <input
                type="text"
                value={digitalAssetUpdateConfig.ruleConfig.name}
                onChange={(e) =>
                  setDigitalAssetUpdateConfig((prev) => ({
                    ...prev,
                    ruleConfig: { ...prev.ruleConfig, name: e.target.value },
                  }))
                }
              />
            </div>

            <div className="actions-modal-field">
              <label>. evaluationMode</label>
              <div style={{ width: "66%" }}>
                <Select
                  options={EvaluationModeOptions}
                  value={EvaluationModeOptions.find(
                    (opt) =>
                      opt.value ===
                      digitalAssetUpdateConfig.ruleConfig.evaluationMode
                  )}
                  onChange={(selected) =>
                    setDigitalAssetUpdateConfig((prev) => ({
                      ...prev,
                      ruleConfig: {
                        ...prev.ruleConfig,
                        evaluationMode: selected?.value || "",
                      },
                    }))
                  }
                  classNamePrefix="react-select"
                />
              </div>
            </div>

            <div className="actions-modal-fields">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  paddingLeft: "20px",
                }}
              >
                <label
                  style={{
                    width: "174px",
                    fontWeight: 500,
                    textAlign: "left",
                    marginBottom: 0,
                  }}
                >
                  . expressions
                </label>
                <button
                  className="actions-crt-add-btn"
                  onClick={() => {
                    setExpressionForm({
                      type: "",
                      lhs: "",
                      rhs: "",
                      validationFailureMessage: "",
                    });
                    setEditingExpressionIndex(null);
                    setShowExpressionsPopup(true);
                  }}
                >
                  +
                </button>
              </div>

              {digitalAssetUpdateConfig.ruleConfig.expressions.map(
                (exp, idx) => (
                  <div key={idx} className="expression-summary-box">
                    <div className="expression-summary-header">
                      <span>Expression</span>
                      <div className="expression-summary-header">
                        <div className="expression-summary-icons">
                          <FaEdit
                            className="expression-icon"
                            title="Edit"
                            onClick={() => handleEditExpression(idx)}
                          />
                          <FaTrash
                            className="expression-icon"
                            title="Delete"
                            onClick={() => handleDeleteExpression(idx)}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="expression-summary-content">
                      <p>Type: {exp.type}</p>
                      <p>LHS: {exp.lhs}</p>
                      <p>RHS: {exp.rhs}</p>
                      {exp.validationFailureMessage && (
                        <p>Message: {exp.validationFailureMessage}</p>
                      )}
                    </div>
                  </div>
                )
              )}
            </div>
          </div>

          <div className="actions-modal-field">
            <label>2. service Config Name</label>
            <input
              type="text"
              value={digitalAssetUpdateConfig.serviceConfigName}
              onChange={(e) =>
                setDigitalAssetUpdateConfig((prev) => ({
                  ...prev,
                  serviceConfigName: e.target.value,
                }))
              }
            />
          </div>

          <div className="actions-modal-field">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "0.5rem",
              }}
            >
              <label style={{ fontWeight: 500 }}>
                3. Required Asset Definition Names
              </label>
            </div>

            <div className="flex flex-col gap-2" style={{ width: "66%" }}>
              {digitalAssetUpdateConfig.requiredAssetDefinitionNames.map(
                (entry, index) => (
                  <div key={index}>
                    <Select
                      inputValue={nameInput}
                      onInputChange={(val, { action }) => {
                        setNameInput(val);
                        if (action === "input-change" && val.length >= 3) {
                          fetchAssetNames(val, {
                            onSuccess: (data) => {
                              setAssetOptions(
                                data.map((item) => ({
                                  label: item.name,
                                  value: item.id,
                                }))
                              );
                            },
                          });
                        }
                        if (action === "clear") {
                          setNameInput("");
                        }
                      }}
                      onChange={(selected) => {
                        if (selected) {
                          setNameInput(selected.label);
                          handleChange("id", selected.value);
                          handleChange("name", selected.label);
                        } else {
                          handleChange("id", "");
                          handleChange("name", "");
                          setNameInput("");
                        }
                      }}
                      value={
                        form.name && form.id
                          ? { label: form.name, value: form.id }
                          : null
                      }
                      options={assetOptions}
                      placeholder="Search by asset name"
                      isClearable
                      isSearchable
                      isLoading={isAssetLoading}
                      classNamePrefix="react-select"
                      noOptionsMessage={() =>
                        nameInput.length < 3
                          ? "Type at least 3 characters"
                          : "No matching assets"
                      }
                    />
                  </div>
                )
              )}
            </div>
          </div>

          <div className="actions-modal">
            <button className="actions-modal-save" onClick={handleSave}>
              Save
            </button>
            <button className="actions-modal-cancel" onClick={onClose}>
              Cancel
            </button>
          </div>
        </div>
        {showExpressionsPopup && (
          <div className="popup-overlay">
            <div className="popup-box">
              <div className="popup-header-action">
                <div className="popup-title-action">Add Expression</div>
                <div
                  onClick={() => setShowExpressionsPopup(false)}
                  className="popup-close-action"
                >
                  ×
                </div>
              </div>

              <div style={{ marginTop: "16px" }}>
                <div className="form-group-action">
                  <label>type</label>
                  <div style={{ width: "50%" }}>
                    <Select
                      options={ExpressionTypeOptions}
                      value={ExpressionTypeOptions.find(
                        (opt) => opt.value === expressionForm.type
                      )}
                      onChange={(selected) =>
                        setExpressionForm((prev) => ({
                          ...prev,
                          type: selected?.value || "",
                        }))
                      }
                      classNamePrefix="react-select"
                    />
                  </div>
                </div>

                <div className="form-group-action">
                  <label>lhs</label>
                  <input
                    type="text"
                    placeholder="Enter lhs"
                    value={expressionForm.lhs}
                    onChange={(e) =>
                      setExpressionForm((prev) => ({
                        ...prev,
                        lhs: e.target.value,
                      }))
                    }
                  />
                </div>

                <div className="form-group-action">
                  <label>rhs</label>
                  <input
                    type="text"
                    placeholder="Enter rhs"
                    value={expressionForm.rhs}
                    onChange={(e) =>
                      setExpressionForm((prev) => ({
                        ...prev,
                        rhs: e.target.value,
                      }))
                    }
                  />
                </div>

                <div className="form-group-action">
                  <label>validationFailureMessage</label>

                  <input
                    type="text"
                    placeholder="Enter message"
                    value={expressionForm.validationFailureMessage}
                    onChange={(e) =>
                      setExpressionForm((prev) => ({
                        ...prev,
                        validationFailureMessage: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              <div className="popup-actions">
                <button
                  className="actions-modal-save"
                  onClick={handleAddOrUpdateExpression}
                >
                  Save
                </button>
                <button
                  className="actions-modal-cancel"
                  onClick={() => setShowExpressionsPopup(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActionsModal;
